/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import buildAutomationListItems from '@/lib/automations/buildAutomationListItems.js';
import buildAutomationRunRows from '@/lib/automations/buildAutomationRunRows.js';
import buildRunStepsFromEvents from '@/lib/automations/buildRunStepsFromEvents.js';
import { workflowRunList, workflows } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import { EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';
import { automationScheduleItems, automationTriggerItems } from '@/redux/selectors/pieveSelectors/automationSharedSelectors.js';
import { automationWorkflows } from '@/redux/selectors/pieveSelectors/automationWorkflowSelectors.js';
import { workflowRunDefinitionSteps, workflowRunFailure, workflowRunStepDisplayNameByStepId, workflowRunStepStatusByStepId, workflowRunTimeMachineEntry } from '@/redux/selectors/pieveSelectors/automationWorkflowRunLogsSelectors.js';
import buildWorkflowRunStepList from '@/lib/automations/buildWorkflowRunStepList.js';

/** Every automation of the active organization in one list, newest change first. */
export const automationListItems = createSelector([automationScheduleItems, automationTriggerItems, automationWorkflows], (schedules, triggers, workflowList) => {
  return buildAutomationListItems({ schedules, triggers, workflows: workflowList });
});

/** One automation of the list, by its kind (`schedules`, `triggers`, `workflows`) and id. */
export const automationListItem = createSelector([automationListItems, (_state, kind) => kind, (_state, _kind, id) => id], (items, kind, id) => {
  return items.find((item) => item.kind === kind && item.id === id) || null;
});

/** A schedule as the API keeps it (cadence, instructions), for its detail page. */
export const automationScheduleDetail = createSelector([automationScheduleItems, (_state, id) => id], (items, id) => {
  return items.find((item) => item.id === id) || null;
});

/** A trigger as the API keeps it (what it watches, instructions), for its detail page. */
export const automationTriggerDetail = createSelector([automationTriggerItems, (_state, id) => id], (items, id) => {
  return items.find((item) => item.id === id) || null;
});

/** The draft of a workflow (the version its steps are edited in), for its detail page. */
export const automationWorkflowDetail = createSelector([workflows, (_state, workflowId) => workflowId], (workflowList, workflowId) => {
  return workflowList.find((workflow) => workflow.workflowId === workflowId && workflow.version === 'draft') || null;
});

/** The run history of one automation, newest first. Schedule and trigger lists hold the last one loaded. */
export const automationRuns = createSelector([(state) => state.pieve.scheduleRunList, (state) => state.pieve.triggerRunList, workflowRunList, (_state, kind) => kind, (_state, _kind, id) => id], (scheduleRuns, triggerRuns, workflowRuns, kind, id) => {
  if (kind === 'schedules') {
    return buildAutomationRunRows({ kind, runs: scheduleRuns.filter((run) => run.scheduleId === id) });
  }

  if (kind === 'triggers') {
    return buildAutomationRunRows({ kind, runs: triggerRuns.filter((run) => run.triggerId === id) });
  }

  return buildAutomationRunRows({ kind, runs: workflowRuns.filter((run) => run.workflowId === id) });
});

/** The steps Pieve took in one run's conversation and the result it reached. */
export const automationRunSteps = createSelector([(state) => state.pieve.automationRunEvents || EMPTY_ARRAY, (_state, threadId) => threadId], (events, threadId) => {
  return buildRunStepsFromEvents(events.filter((event) => event.threadId === threadId));
});

/** Whether a run's conversation events have been loaded at least once. */
export const automationRunEventsLoaded = (state, threadId) => (state.pieve.automationRunEvents || EMPTY_ARRAY).some((event) => event.threadId === threadId);

/** The steps of one workflow run with what happened at each, and the run's summary as its result. */
export const automationWorkflowRunSteps = createSelector([workflowRunTimeMachineEntry, workflowRunDefinitionSteps, workflowRunStepDisplayNameByStepId, workflowRunStepStatusByStepId, workflowRunFailure], (entry, steps, nameByStepId, statusByStepId, failure) => {
  return buildWorkflowRunStepList({ run: entry?.run, steps, nameByStepId, statusByStepId, failure });
});
